import { Group, Tooltip, useMantineTheme, Button, Menu } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';

export type EditorViewType = 'outline' |
'narrative' | 'documents' | 'compliance' | 'assistant';
export type Position = 'left' | 'right';

export interface ViewSelectorProps {
  isFullScreen: boolean
  position: Position
  viewTypes: EditorViewType[]
  handleOptionSubmit: (value: string, position: Position) => void
  enabledViewTypes: EditorViewType[]
  currentViewType: EditorViewType
  rightSection?: React.ReactNode
};

export const viewTypeToLabelMap: Record<EditorViewType, string> = {
  outline: 'Outline',
  narrative: 'Narrative',
  documents: 'Documents',
  compliance: 'Compliance',
  assistant: 'Resource Assistant'
};

const EditorPaneViewSelector: React.FC<ViewSelectorProps> = (props: ViewSelectorProps) => {
  const {
    position,
    viewTypes,
    isFullScreen,
    currentViewType,
    handleOptionSubmit,
    enabledViewTypes,
    rightSection
  } = props;
  const { colors } = useMantineTheme();
  const viewSelectorData = viewTypes.map((viewType) => ({
    value: viewType,
    label: viewTypeToLabelMap[viewType],
    disabled: !enabledViewTypes.includes(viewType)
  }));

  const disabled = enabledViewTypes.length === 0;

  let borderRadius;
  if (isFullScreen) {
    borderRadius = 8;
  } else if (position === 'left') {
    borderRadius = '8px 0 0 0';
  } else if (position === 'right') {
    borderRadius = '0 8px 0 0';
  }

  return (
    <Group
      mih={64}
      py="5"
      px={4}
      bg={colors.lightPurple[0]}
      style={{
        borderRadius,
        border: `1px ${colors.gray[2]} solid`,
        borderLeft: (position === 'right') ? '' : `1px ${colors.gray[2]} solid`,
        borderRight: (position === 'left' && !isFullScreen) ? '' : `1px ${colors.gray[2]} solid`
      }}
      justify='space-between'
    >
      <Menu position='bottom-start'>
        <Menu.Target>
          <Tooltip
            events={{
              hover: enabledViewTypes.length === 0,
              focus: false,
              touch: false
            }}
            label='Save changes before switching views.'
          >
            <Button
              variant='subtle'
              px='8px'
              disabled={enabledViewTypes.length === 0}
              styles={{
                section: { marginLeft: 2 },
                label: {
                  fontSize: 16,
                  fontWeight: 500
                }
              }}
              rightSection={<IconChevronDown />}
              c={!disabled ? colors.darkPurple[9] : undefined}
            >
              {viewTypeToLabelMap[currentViewType]}
            </Button>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          {viewSelectorData.map((item) => (
            <Menu.Item
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              onClick={() => { handleOptionSubmit(item.value, position); }}
            >
              {item.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      {rightSection}
    </Group >
  );
};

export default EditorPaneViewSelector;
